import { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, Monitor, Smartphone, LogOut } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BTN, BTN_GHOST, isCancel } from "@/components/account/ConfirmIdentity";
import { Section } from "@/components/account/ProfileSection";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.2 14.6 2.2 12 2.2 6.6 2.2 2.2 6.6 2.2 12s4.4 9.8 9.8 9.8c5.7 0 9.4-4 9.4-9.6 0-.6-.1-1.1-.2-1.6H12z" /></svg>
);

function PasswordForm({ account, getToken, onDone }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const has = account.has_password;
  const submit = async (e) => {
    e.preventDefault();
    if (next !== again) { toast.error("The two new passwords don't match."); return; }
    setBusy(true);
    try {
      const body = { new_password: next, current_password: cur };
      if (!has) body.reauth_token = await getToken();
      await http.post("/account/password", body);
      toast.success(has ? "Password changed. Your other devices were signed out." : "Password added");
      onDone();
    } catch (err) { if (!isCancel(err)) toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const field = (v, set, label, id, ac) => <Input type="password" value={v} onChange={(e) => set(e.target.value)} placeholder={label} aria-label={label} autoComplete={ac} className="bg-white/5 border-white/10" data-testid={id} />;
  return (
    <form onSubmit={submit} className="space-y-2.5 pt-1" data-testid="password-form">
      {has && field(cur, setCur, "Current password", "password-current-input", "current-password")}
      {field(next, setNext, "New password (at least 6 characters)", "password-new-input", "new-password")}
      {field(again, setAgain, "Type the new password again", "password-again-input", "new-password")}
      <div className="flex gap-2">
        <button type="submit" disabled={busy || next.length < 6 || (has && !cur)} className={BTN} data-testid="password-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{has ? "Change password" : "Add password"}</button>
        <button type="button" onClick={onDone} className={BTN_GHOST} data-testid="password-cancel-btn">Cancel</button>
      </div>
    </form>
  );
}

function PasswordRow({ account, getToken, onChanged }) {
  const [open, setOpen] = useState(false);
  const googleUsable = account.google.used && account.google.enabled;
  const remove = async () => {
    if (!window.confirm("Turn off password sign-in? You'll only be able to sign in with Google.")) return;
    try { await http.post("/account/password/remove", { reauth_token: await getToken() }); toast.success("Password sign-in turned off"); onChanged(); }
    catch (e) { if (!isCancel(e)) toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-2" data-testid="signin-password-row">
      <div className="flex flex-wrap items-center gap-3">
        <KeyRound className="h-5 w-5 text-sky-300 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-neutral-100">Email and password</p>
          <p className="text-xs text-neutral-500" data-testid="signin-password-status">{account.has_password ? "On" : "No password yet"}</p>
        </div>
        {!open && <button onClick={() => setOpen(true)} className={BTN_GHOST} data-testid="password-open-btn">{account.has_password ? "Change" : "Add password"}</button>}
        {!open && account.has_password && googleUsable && <button onClick={remove} className="text-xs text-red-300 hover:underline" data-testid="password-remove-btn">Turn off</button>}
      </div>
      {open && <PasswordForm account={account} getToken={getToken} onDone={() => { setOpen(false); onChanged(); }} />}
    </div>
  );
}

function GoogleRow({ account, getToken, onChanged }) {
  const [busy, setBusy] = useState(false);
  const { used, enabled } = account.google;
  let status = "Off";
  if (enabled) status = used ? "On" : `Not used yet. Choose "Continue with Google" with ${account.email} to start.`;
  const toggle = async (v) => {
    setBusy(true);
    try { await http.put("/account/google", { enabled: v, reauth_token: await getToken() }); toast.success(v ? "Google sign-in turned on" : "Google sign-in turned off"); onChanged(); }
    catch (e) { if (!isCancel(e)) toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const locked = account.is_owner || (enabled && !account.has_password);
  return (
    <div className="flex items-center gap-3" data-testid="signin-google-row">
      <GoogleIcon />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-neutral-100">Google</p>
        <p className="text-xs text-neutral-500" data-testid="signin-google-status">{status}{locked && enabled ? " · Needed so you can always sign in" : ""}</p>
      </div>
      <Switch checked={enabled} disabled={busy || locked} onCheckedChange={toggle} aria-label="Allow Google sign-in" data-testid="signin-google-switch" />
    </div>
  );
}

const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 10) return "Active now";
  if (m < 60) return `Active ${m} minutes ago`;
  if (m < 1440) return `Active ${Math.round(m / 60)} hours ago`;
  return `Active ${new Date(iso).toLocaleDateString()}`;
};

function Devices() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => http.get("/account/sessions").then((r) => setRows(r.data)).catch((e) => { toast.error(formatError(e)); setRows([]); }), []);
  useEffect(() => { load(); }, [load]);
  const out = async (sid) => { try { await http.delete(`/account/sessions/${sid}`); toast.success("Device signed out"); load(); } catch (e) { toast.error(formatError(e)); } };
  const outAll = async () => {
    if (!window.confirm("Sign out of every other device?")) return;
    try { await http.post("/account/sessions/revoke-others"); toast.success("Other devices signed out"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-2" data-testid="devices-list">
      <p className="text-sm text-neutral-400">Signed-in devices</p>
      {!rows && <p className="text-xs text-neutral-500">Loading…</p>}
      {rows?.map((d) => {
        const Icon = /iPhone|Android|iPad/.test(d.device) ? Smartphone : Monitor;
        return (
          <div key={d.sid} className="flex items-center gap-3 py-2 border-b border-white/[0.06] last:border-0" data-testid={`device-${d.sid}`}>
            <Icon className="h-5 w-5 text-neutral-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-neutral-100 truncate">{d.device}{d.current && <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300" data-testid="device-current-badge">This device</span>}</p>
              <p className="text-xs text-neutral-500 truncate">{ago(d.last_seen)}{d.ip ? ` · ${d.ip}` : ""}{d.kind === "google" ? " · Google" : ""}</p>
            </div>
            {!d.current && <button onClick={() => out(d.sid)} className="text-xs text-neutral-400 hover:text-white" data-testid={`device-signout-${d.sid}`}>Sign out</button>}
          </div>
        );
      })}
      {rows?.some((d) => !d.current) && <button onClick={outAll} className={BTN_GHOST} data-testid="devices-signout-all-btn"><LogOut className="h-4 w-4" />Sign out of all other devices</button>}
    </div>
  );
}

export function SecuritySection(props) {
  return (
    <Section title="Sign-in and security" text="You always keep at least one way to sign in." testid="account-security">
      <PasswordRow {...props} />
      <GoogleRow {...props} />
      <Devices />
    </Section>
  );
}
